import { alpha, darken, lighten } from "@mui/material/styles";
import type { ApiTheme, ThemeColorSource, ThemeModeColors } from "@/types/theme";
import { resolveGoogleFontFamilyName } from "./googleFonts";
import { themeColors, type ThemePaletteColors } from "./themeColors";

export type ResolvedThemeMode = "light" | "dark";

/** Mutable palette after merging API theme into platform defaults. */
export type MergedColors = ThemePaletteColors;

const LIGHT_KEYS = [
  "background",
  "foreground",
  "card",
  "cardForeground",
  "muted",
  "mutedForeground",
  "border",
] as const;

const DARK_KEYS = [...LIGHT_KEYS] as const;

const SHADOW_VARS: Record<ResolvedThemeMode, Record<string, string>> = {
  light: {
    "--shadow-sm":
      "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
    "--shadow-md":
      "0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)",
    "--shadow-lg":
      "0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)",
    "--color-info": "#2196f3",
  },
  dark: {
    "--shadow-sm":
      "0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)",
    "--shadow-md":
      "0 3px 6px rgba(0, 0, 0, 0.4), 0 3px 6px rgba(0, 0, 0, 0.5)",
    "--shadow-lg":
      "0 10px 20px rgba(0, 0, 0, 0.5), 0 6px 6px rgba(0, 0, 0, 0.6)",
    "--color-info": "#64b5f6",
  },
};

/** Platform CSS vars derived from themeColors (same values as themes/services.py DEFAULT_THEME). */
function platformCssVars(mode: ResolvedThemeMode): Record<string, string> {
  const c = themeColors[mode];
  const secondary = c.secondary ?? "#06D6A0";
  return {
    ...SHADOW_VARS[mode],
    "--color-primary": c.primary,
    "--color-primary-dark": darken(c.primary, 0.15),
    "--color-primary-light": lighten(c.primary, 0.2),
    "--color-secondary": secondary,
    "--color-secondary-dark": darken(secondary, 0.15),
    "--color-secondary-light": lighten(secondary, 0.2),
    "--color-background": c.background,
    "--color-surface": c.card,
    "--color-surface-variant": c.muted,
    "--color-surface-elevated": c.card,
    "--color-text-primary": c.foreground,
    "--color-text-on-surface": c.cardForeground,
    "--color-text-secondary": c.mutedForeground,
    "--color-text-disabled": mode === "dark" ? "#666666" : "#bdbdbd",
    "--color-border": c.border,
    "--color-success": c.success,
    "--color-warning": c.warning,
    "--color-error": c.danger,
    "--color-on-primary": c.primaryForeground,
  };
}

function isCustomSource(
  sources: Partial<Record<string, ThemeColorSource>> | undefined,
  key: string
): boolean {
  return sources?.[key] === "custom";
}

/** Slightly distinct fill for inputs/chips on branded surfaces (never wash out to white). */
export function deriveMutedSurface(surfaceHex: string): string {
  const hex = surfaceHex.replace("#", "").trim();
  if (hex.length !== 6) return surfaceHex;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 0.55 ? darken(surfaceHex, 0.08) : lighten(surfaceHex, 0.12);
}

/** Subtle borders/dividers on branded surfaces. */
export function deriveBorderColor(
  foregroundHex: string,
  strength: "light" | "default" | "strong" = "default"
): string {
  const opacity =
    strength === "light" ? 0.1 : strength === "strong" ? 0.22 : 0.16;
  return alpha(foregroundHex, opacity);
}

function applyModeSurfaces(
  merged: MergedColors,
  modeColors: ThemeModeColors | undefined,
  sources: Partial<Record<string, ThemeColorSource>> | undefined,
  mode: ResolvedThemeMode
): void {
  if (!modeColors) return;

  const platform = themeColors[mode];

  if (modeColors.background) merged.background = modeColors.background;
  if (modeColors.foreground) merged.foreground = modeColors.foreground;
  if (modeColors.cardForeground) merged.cardForeground = modeColors.cardForeground;
  if (modeColors.mutedForeground) {
    merged.mutedForeground = modeColors.mutedForeground;
  }

  if (isCustomSource(sources, "muted") && modeColors.muted?.trim()) {
    merged.muted = modeColors.muted.trim();
  }
  if (isCustomSource(sources, "border") && modeColors.border?.trim()) {
    merged.border = modeColors.border.trim();
  }

  if (isCustomSource(sources, "card") && modeColors.card?.trim()) {
    merged.card = modeColors.card.trim();
  } else if (isCustomSource(sources, "background") && modeColors.background?.trim()) {
    merged.card = modeColors.background.trim();
  } else if (modeColors.card?.trim()) {
    merged.card = modeColors.card.trim();
  }

  const surface = merged.card?.trim();
  const hasBrandedSurface =
    isCustomSource(sources, "background") || isCustomSource(sources, "card");

  if (!isCustomSource(sources, "muted") && hasBrandedSurface && surface) {
    merged.muted = deriveMutedSurface(surface);
  }

  if (!isCustomSource(sources, "border") && hasBrandedSurface) {
    merged.border = deriveBorderColor(merged.foreground, "default");
  }

  // Legacy API without *Sources: infer from platform hex (older backends).
  if (!sources || Object.keys(sources).length === 0) {
    applyLegacyPlatformInference(merged, modeColors, platform);
  }
}

/** @deprecated Used only when API omits lightSources / darkSources */
function applyLegacyPlatformInference(
  merged: MergedColors,
  modeColors: ThemeModeColors,
  platform: ThemePaletteColors
): void {
  const isPlatform = (value: string | undefined, ref: string) =>
    !value?.trim() || value.trim().toUpperCase() === ref.toUpperCase();

  const customBg = modeColors.background?.trim();
  const customCard = modeColors.card?.trim();
  const cardIsPlatform = isPlatform(customCard, platform.card);

  if (customCard && !cardIsPlatform) {
    merged.card = customCard;
  } else if (customBg && !isPlatform(customBg, platform.background)) {
    merged.card = customBg;
  }

  const surface = merged.card?.trim();
  const hasBranded =
    !!surface && !isPlatform(surface, platform.card);

  if (hasBranded && isPlatform(merged.muted, platform.muted)) {
    merged.muted = deriveMutedSurface(surface!);
  }
  if (hasBranded && isPlatform(merged.border, platform.border)) {
    merged.border = deriveBorderColor(merged.foreground, "default");
  }
}

function buildCssVars(
  mode: ResolvedThemeMode,
  colors: MergedColors,
  apiTheme?: ApiTheme | null
): Record<string, string> {
  const vars = { ...platformCssVars(mode) };

  vars["--color-primary"] = colors.primary;
  vars["--color-primary-light"] = lighten(colors.primary, 0.2);
  vars["--color-primary-dark"] = darken(colors.primary, 0.15);
  vars["--color-on-primary"] = colors.primaryForeground;

  const secondary = colors.secondary ?? themeColors[mode].secondary ?? "#06D6A0";
  vars["--color-secondary"] = secondary;
  vars["--color-secondary-light"] = lighten(secondary, 0.2);
  vars["--color-secondary-dark"] = darken(secondary, 0.15);

  vars["--color-background"] = colors.background;
  vars["--color-surface"] = colors.card;
  vars["--color-surface-elevated"] = colors.card;
  vars["--color-surface-variant"] = colors.muted;
  vars["--color-text-primary"] = colors.foreground;
  vars["--color-text-on-surface"] = colors.cardForeground;
  vars["--color-text-secondary"] = colors.mutedForeground;
  vars["--color-border"] = colors.border;
  vars["--color-border-light"] = deriveBorderColor(colors.foreground, "light");
  vars["--color-border-dark"] = deriveBorderColor(colors.foreground, "strong");

  vars["--color-success"] = colors.success;
  vars["--color-warning"] = colors.warning;
  vars["--color-error"] = colors.danger;

  Object.assign(vars, buildTypographyCssVars(apiTheme));

  return vars;
}

const TYPOGRAPHY_SIZE_CSS_KEYS = ["xs", "sm", "md", "lg", "xl"] as const;

/** MUI `fontFamily` stack from API typography (Premium tier when not masked). */
export function resolveWebFontFamily(apiTheme?: ApiTheme | null): string {
  const displayName = apiTheme?.typography?.fontFamilyName?.trim();
  if (displayName) {
    return `"${displayName}", "Roboto", "Helvetica", "Arial", sans-serif`;
  }
  const regular = apiTheme?.typography?.fontFamily?.regular;
  if (regular?.trim()) {
    const base = resolveGoogleFontFamilyName(
      regular.replace(/[-_](Regular|Medium|SemiBold|Bold)$/i, "").trim(),
    );
    if (base) {
      return `"${base}", "Roboto", "Helvetica", "Arial", sans-serif`;
    }
  }
  return '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
}

/** Google Font family name for dynamic @font-face loading. */
export function resolveBrandedGoogleFontName(apiTheme?: ApiTheme | null): string {
  const displayName = apiTheme?.typography?.fontFamilyName?.trim();
  if (displayName) return displayName;
  const regular = apiTheme?.typography?.fontFamily?.regular;
  if (!regular?.trim()) return "";
  return resolveGoogleFontFamilyName(
    regular.replace(/[-_](Regular|Medium|SemiBold|Bold)$/i, "").trim(),
  );
}

/** Override fluid `--fs-*` tokens when entreprise typography sizes are set. */
export function buildTypographyCssVars(
  apiTheme?: ApiTheme | null,
): Record<string, string> {
  const sizes = apiTheme?.typography?.sizes;
  if (!sizes) return {};
  const vars: Record<string, string> = {};
  for (const key of TYPOGRAPHY_SIZE_CSS_KEYS) {
    const px = sizes[key];
    if (typeof px === "number" && px > 0) {
      vars[`--fs-${key}`] = `${px / 16}rem`;
    }
  }
  return vars;
}

export interface ResolvedThemeTokens {
  colors: MergedColors;
  cssVars: Record<string, string>;
}

/**
 * Single source of truth: merge API theme + platform defaults for MUI and CSS variables.
 */
export function resolveThemeTokens(
  mode: ResolvedThemeMode,
  apiTheme?: ApiTheme | null
): ResolvedThemeTokens {
  const baseKey = mode === "dark" ? "dark" : "light";
  const merged: MergedColors = { ...themeColors[baseKey] };

  if (!apiTheme) {
    return { colors: merged, cssVars: buildCssVars(mode, merged, null) };
  }

  if (apiTheme.primary) merged.primary = apiTheme.primary;
  if (apiTheme.primaryForeground) {
    merged.primaryForeground = apiTheme.primaryForeground;
  }
  merged.secondary =
    apiTheme.secondary ?? themeColors[baseKey].secondary ?? "#06D6A0";

  if (mode === "light" && apiTheme.light) {
    applyModeSurfaces(merged, apiTheme.light, apiTheme.lightSources, "light");
  }
  if (mode === "dark" && apiTheme.dark) {
    applyModeSurfaces(merged, apiTheme.dark, apiTheme.darkSources, "dark");
  }

  if (apiTheme.accent) {
    if (apiTheme.accent.success) merged.success = apiTheme.accent.success;
    if (apiTheme.accent.warning) merged.warning = apiTheme.accent.warning;
    if (apiTheme.accent.danger) merged.danger = apiTheme.accent.danger;
  }

  return {
    colors: merged,
    cssVars: buildCssVars(mode, merged, apiTheme),
  };
}

export { LIGHT_KEYS, DARK_KEYS };
